/**
 * Display vocabulary for guided answer ids (internal_staff, mvp_build, batch, …).
 *
 * Question bank (agenticGuidedQuestionBank.ts) and capability cards are the source.
 * Regenerate this map when option ids change — eval:user-facing-copy fails if a
 * bank option is missing. Do not hand-edit ANSWER_LABELS_BY_QUESTION.
 *
 * Flat labels exist only when every question agrees on the same wording.
 * Collisions (cost, large, none, …) resolve through the question id.
 */

const ANSWER_LABELS_BY_QUESTION = {
  'agent_cost_attr': {
    'chargeback': { en: 'Org chargeback / cost center', pl: 'Chargeback / cost center' },
    'none': { en: 'No attribution yet', pl: 'Brak atrybucji' },
    'per_agent': { en: 'Per-agent / specialty split', pl: 'Podział per agent / specjalizacja' },
    'per_run': { en: 'Per-run totals', pl: 'Sumy per-run' },
  },
  'agent_firewall': {
    'full_policy': { en: 'Full policy (prompt + tools + egress)', pl: 'Pełna polityka (prompt + tooli + egress)' },
    'none': { en: 'None yet', pl: 'Jeszcze brak' },
    'prompt_only': { en: 'Prompt / output filters', pl: 'Filtry prompt / output' },
    'tool_sandbox': { en: 'Tool sandbox + allow-list', pl: 'Sandbox tooli + allow-list' },
  },
  'agent_gov_tier': {
    'pilot': { en: 'Pilot / experiment', pl: 'Pilot / eksperyment' },
    'production': { en: 'Broad production', pl: 'Szeroka produkcja' },
    'regulated': { en: 'Regulated / high-stakes', pl: 'Regulowane / wysokie stawki' },
    'team': { en: 'Team production (limited)', pl: 'Produkcja zespołowa (ograniczona)' },
  },
  'agent_memory': {
    'external_bank': { en: 'External memory bank / RAG', pl: 'Zewnętrzny bank pamięci / RAG' },
    'long_term': { en: 'Long-term user / org', pl: 'Long-term użytkownik / org' },
    'session': { en: 'Session-only', pl: 'Tylko sesja' },
    'short_term': { en: 'Short-term run / thread', pl: 'Short-term run / wątek' },
  },
  'ai_data_scale': {
    'corpus_large': { en: 'Large — 500k+', pl: 'Duży — 500k+' },
    'corpus_medium': { en: 'Medium — ~10k–500k', pl: 'Średni — ~10k–500k' },
    'corpus_small': { en: 'Small — up to ~10k docs / chunks', pl: 'Mały — do ~10k dokumentów / chunków' },
    'unknown_corpus': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'ai_serving': {
    'batch_only': { en: 'Mostly offline / batch', pl: 'Głównie offline / batch' },
    'dedicated_endpoint': { en: 'Dedicated inference endpoint', pl: 'Dedykowany endpoint inferencji' },
    'hybrid': { en: 'Hybrid (API + self-host)', pl: 'Hybryda (API + self-host)' },
    'managed_api': { en: 'Managed LLM APIs (usage-based)', pl: 'Zarządzane API LLM (pay-per-use)' },
    'self_hosted': { en: 'Self-hosted models / GPUs', pl: 'Modele self-hosted / GPU' },
  },
  'ai_style': {
    'agents': { en: 'Multi-agent / tool use', pl: 'Wieloetapowi agenci z narzędziami' },
    'batch': { en: 'Batch / nearline ML', pl: 'ML batch / nearline' },
    'mlops': { en: 'MLOps train → serve', pl: 'MLOps train → serve' },
    'online': { en: 'Online inference', pl: 'Inferencja online' },
    'quote': { en: 'Quote / estimate advisor', pl: 'Doradca wyceny / estimate' },
    'rag': { en: 'RAG / grounded assistant', pl: 'RAG / asystent na dokumentach' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'ai_volume': {
    'batch_jobs': { en: 'Batch / nightly jobs', pl: 'Batch / joby nocne' },
    'heavy': { en: 'Heavy — tens of thousands+ / day', pl: 'Ciężki — dziesiątki tysięcy+ / dzień' },
    'pilot': { en: 'Pilot — tens of requests / day', pl: 'Pilot — dziesiątki requestów / dzień' },
    'product': { en: 'Product feature — thousands+ / day', pl: 'Feature produktu — tysiące+ / dzień' },
    'team': { en: 'Team tool — hundreds / day', pl: 'Narzędzie zespołu — setki / dzień' },
    'unknown_volume': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'ai_workload': {
    'agents': { en: 'Multi-step agents / tool use', pl: 'Agenci wieloetapowi z narzędziami' },
    'batch': { en: 'Batch / nearline ML', pl: 'ML batch / nearline' },
    'copilot': { en: 'In-product copilot', pl: 'Copilot w produkcie' },
    'find_data': { en: 'Search & analytics', pl: 'Wyszukiwanie i analityka' },
    'genai': { en: 'Generative / assistants', pl: 'Generatywne / asystenci' },
    'ml_online': { en: 'Online & batch ML', pl: 'ML online i batch' },
    'online': { en: 'Online ML inference', pl: 'Inferencja ML online' },
    'pipeline_ml': { en: 'Batch / offline ML (cost focus)', pl: 'Batch / offline ML (koszty)' },
    'quote': { en: 'Quote / estimate advisor', pl: 'Doradca wyceny / estimate' },
    'rag': { en: 'RAG / grounded assistant', pl: 'RAG / asystent na dokumentach' },
    'search': { en: 'Enterprise search (ACL)', pl: 'Wyszukiwanie enterprise (ACL)' },
  },
  'api_style': {
    'api_led': { en: 'API-led (Experience / Process / System)', pl: 'Warstwowe API (doświadczenie / proces / system)' },
    'apim': { en: 'API Management product', pl: 'Produkt API Management' },
    'async_queue': { en: 'Async queue workers', pl: 'Async queue workers' },
    'graphql': { en: 'GraphQL federation', pl: 'GraphQL federation' },
    'serverless': { en: 'Serverless / HTTP APIs', pl: 'Serverless / HTTP API' },
    'soap_odata': { en: 'SOAP / OData (ERP, CRM, Microsoft)', pl: 'SOAP / OData (ERP, CRM, Microsoft)' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'capability_scope': {
    'admin_backoffice': { en: 'Admin / back-office', pl: 'Admin / back-office' },
    'admin_console': { en: 'Admin / ops console', pl: 'Konsola admin / ops' },
    'agents_tools': { en: 'Agents & tool use', pl: 'Agenci i narzędzia' },
    'alerting': { en: 'Rules & alerting', pl: 'Reguły i alerty' },
    'api_auth': { en: 'API authentication', pl: 'Uwierzytelnianie API' },
    'api_connectors': { en: 'Sync API connectors', pl: 'Konektory API sync' },
    'api_facade': { en: 'API facade / routing', pl: 'Fasada API / routing' },
    'api_versioning': { en: 'Versioning & lifecycle', pl: 'Wersjonowanie i lifecycle' },
    'approvals': { en: 'Approvals & routing', pl: 'Akceptacje i routing' },
    'async_webhooks': { en: 'Async / webhooks', pl: 'Async / webhooki' },
    'audit_compliance': { en: 'Audit & compliance trail', pl: 'Audyt i ślad compliance' },
    'bi_reporting': { en: 'BI & self-serve analytics', pl: 'BI i analityka self-serve' },
    'billing_subscriptions': { en: 'Billing & subscriptions', pl: 'Billing i subskrypcje' },
    'cart_checkout': { en: 'Cart & checkout', pl: 'Koszyk i checkout' },
    'core_workflows': { en: 'Core user workflows', pl: 'Główne ścieżki użytkownika' },
    'data_governance': { en: 'Data governance', pl: 'Governance danych' },
    'data_replication': { en: 'Data replication & sync', pl: 'Replikacja i sync danych' },
    'decommission': { en: 'Legacy decommission', pl: 'Wycofanie legacy' },
    'developer_portal': { en: 'Developer portal', pl: 'Portal deweloperski' },
    'device_management': { en: 'Device management', pl: 'Zarządzanie urządzeniami' },
    'device_telemetry': { en: 'Device telemetry', pl: 'Telemetria urządzeń' },
    'documents': { en: 'Documents & attachments', pl: 'Dokumenty i załączniki' },
    'dual_run_validation': { en: 'Dual-run validation', pl: 'Walidacja dual-run' },
    'edge_processing': { en: 'Edge processing', pl: 'Przetwarzanie edge' },
    'eval_monitoring': { en: 'Eval & monitoring', pl: 'Ewaluacja i monitoring' },
    'event_routing': { en: 'Event routing', pl: 'Routing zdarzeń' },
    'external_integrations': { en: 'External integrations', pl: 'Integracje zewnętrzne' },
    'forms_crud': { en: 'Forms & CRUD workflows', pl: 'Formularze i CRUD' },
    'guaranteed_delivery': { en: 'Guaranteed delivery', pl: 'Gwarantowane dostarczenie' },
    'guardrails': { en: 'Guardrails & safety', pl: 'Guardrails i bezpieczeństwo' },
    'human_review': { en: 'Human review loop', pl: 'Pętla human review' },
    'identity_cutover': { en: 'Identity / SSO cutover', pl: 'Cutover tożsamości / SSO' },
    'ingestion_etl': { en: 'Ingestion & ETL', pl: 'Ingestia i ETL' },
    'integration_ops': { en: 'Integration observability', pl: 'Integration observability' },
    'inventory_discovery': { en: 'Estate inventory', pl: 'Inwentaryzacja parku' },
    'knowledge_ingestion': { en: 'Knowledge ingestion', pl: 'Ingestia wiedzy' },
    'ml_features': { en: 'ML feature serving', pl: 'Feature serving ML' },
    'mobile_backend': { en: 'Mobile backend / BFF', pl: 'Backend mobilny / BFF' },
    'mobile_ui': { en: 'Mobile client UX', pl: 'UX klienta mobilnego' },
    'notifications': { en: 'Notifications', pl: 'Powiadomienia' },
    'offline_sync': { en: 'Offline & sync', pl: 'Offline i synchronizacja' },
    'order_fulfillment': { en: 'Orders & fulfillment', pl: 'Zamówienia i fulfillment' },
    'payments': { en: 'Payments', pl: 'Płatności' },
    'product_api': { en: 'Product API for integrators', pl: 'API produktu dla integratorów' },
    'product_catalog': { en: 'Product catalog & search', pl: 'Katalog i wyszukiwanie' },
    'promotions': { en: 'Promotions & loyalty', pl: 'Promocje i lojalność' },
    'push_notifications': { en: 'Push notifications', pl: 'Powiadomienia push' },
    'rate_limits': { en: 'Rate limits & quotas', pl: 'Limity i quota' },
    'rbac': { en: 'Roles & permissions', pl: 'Role i uprawnienia' },
    'reporting': { en: 'Dashboards & reporting', pl: 'Dashboardy i raporty' },
    'reporting_insights': { en: 'Reporting / insights', pl: 'Raportowanie / insighty' },
    'retrieval_rag': { en: 'Retrieval / RAG', pl: 'Retrieval / RAG' },
    'scope_tbd': { en: 'Still deciding / TBD', pl: 'Jeszcze ustalamy / TBD' },
    'seller_portal': { en: 'Seller / vendor portal', pl: 'Portal sprzedawcy' },
    'streaming_cdc': { en: 'Streaming / CDC', pl: 'Streaming / CDC' },
    'tenant_workspace': { en: 'Tenants / workspaces', pl: 'Tenanty / workspace' },
    'transformations': { en: 'Transformations & mapping', pl: 'Transformacje i mapowanie' },
    'user_accounts': { en: 'User accounts', pl: 'Konta użytkowników' },
    'user_auth': { en: 'User sign-up & login', pl: 'Rejestracja i logowanie' },
    'warehouse_model': { en: 'Warehouse / lake modeling', pl: 'Modelowanie warehouse / lake' },
  },
  'commerce_style': {
    'headless': { en: 'Headless / multi-channel', pl: 'Handel bez gotowego frontu (headless)' },
    'marketplace': { en: 'Two-sided marketplace', pl: 'Marketplace dwustronny' },
    'omnichannel': { en: 'Omnichannel + stores / POS', pl: 'Omnichannel + sklepy / POS' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
    'transactional': { en: 'Transactional web', pl: 'Web transakcyjny' },
  },
  'compare_axis': {

  },
  'constraints': {
    'cloud_aws': { en: 'Preferred cloud: AWS', pl: 'Preferowana chmura: AWS' },
    'cloud_azure': { en: 'Preferred cloud: Azure', pl: 'Preferowana chmura: Azure' },
    'cloud_gcp': { en: 'Preferred cloud: GCP', pl: 'Preferowana chmura: GCP' },
    'cloud_oracle': { en: 'Preferred cloud: Oracle / OCI', pl: 'Preferowana chmura: Oracle / OCI' },
    'data_residency': { en: 'Data residency / sovereignty', pl: 'Lokalizacja i suwerenność danych' },
    'delivery_deadline': { en: 'Hard delivery deadline / promo go-live', pl: 'Twardy deadline / go-live promocji' },
    'locked_data_estate': { en: 'Existing data platforms stay (warehouse / lake / streaming)', pl: 'Istniejące platformy danych zostają (hurtownia / lake / streaming)' },
    'eu_ai_act': { en: 'EU AI Act in scope', pl: 'Akt o AI UE w zakresie' },
    'gdpr': { en: 'GDPR / personal data', pl: 'RODO / dane osobowe' },
    'none': { en: 'None / not sure', pl: 'Brak / nie wiem' },
    'on_prem': { en: 'On-prem / private cloud required', pl: 'Wymagane on-premise lub prywatna chmura' },
    'regulated': { en: 'Regulated industry (finance/health)', pl: 'Branża regulowana (finanse/zdrowie)' },
    'soc2': { en: 'SOC 2 controls expected', pl: 'Oczekiwane kontrole SOC 2' },
    'team_small': { en: 'Very small team (1–3 people)', pl: 'Bardzo mały zespół (1–3 osoby)' },
  },
  'data_style': {
    'cdc': { en: 'CDC / streaming platform', pl: 'Platforma CDC / streaming' },
    'etl_batch': { en: 'ETL & batch jobs', pl: 'ETL i joby batch' },
    'feature_store': { en: 'Shared feature store', pl: 'Wspólny feature store' },
    'lakehouse': { en: 'Lakehouse (open tables + SQL)', pl: 'Lakehouse (otwarte tabele + SQL)' },
    'mesh': { en: 'Data mesh (domain products)', pl: 'Data mesh (produkty domenowe)' },
    'pipeline': { en: 'Central analytics pipeline', pl: 'Centralny pipeline analityczny' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'distributed_style': {
    'cqrs': { en: 'CQRS + event-sourced core', pl: 'CQRS + event sourcing' },
    'edge': { en: 'Edge compute fabric', pl: 'Warstwa edge compute' },
    'multi_region': { en: 'Multi-region active-active', pl: 'Multi-region active-active' },
    'service_mesh': { en: 'Zero-trust service mesh', pl: 'Service mesh zero-trust' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'estimate_focus': {
    'budget_gate': { en: 'Budget / funding gate', pl: 'Budżet / bramka finansowania' },
    'team_size': { en: 'Team size / hiring plan', pl: 'Wielkość zespołu / hiring' },
    'timeline': { en: 'Timeline / go-live pressure', pl: 'Termin / presja go-live' },
    'vendor_compare': { en: 'Build vs buy / vendor compare', pl: 'Build vs buy / porównanie vendorów' },
  },
  'hld_paste': {

  },
  'integration_landscape': {
    'data_warehouse': { en: 'Data warehouse / analytics', pl: 'Hurtownia danych / analityka' },
    'erp_crm': { en: 'ERP / CRM (SAP, Oracle, Dynamics…)', pl: 'ERP / CRM (SAP, Oracle, Dynamics…)' },
    'identity_provider': { en: 'Corporate identity / SSO', pl: 'Tożsamość korporacyjna / SSO' },
    'legacy_onprem': { en: 'Legacy on-prem systems', pl: 'Legacy on-prem' },
    'none': { en: 'None / standalone (for now)', pl: 'Brak / samodzielny (na razie)' },
    'payments': { en: 'Payments / billing', pl: 'Płatności / billing' },
    'unknown_integration': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'integration_style': {
    'api_led': { en: 'Sync APIs (REST / SOAP / OData)', pl: 'Sync API (REST / SOAP / OData)' },
    'event_hub': { en: 'Event-driven integration hub', pl: 'Event-driven hub integracyjny' },
    'messaging_bus': { en: 'Enterprise messaging bus', pl: 'Firmowa szyna komunikatów' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'transfer_plane': {
    'files_mft': { en: 'Files / MFT / SFTP drops', pl: 'Pliki / MFT / zrzuty SFTP' },
    'api_sync': { en: 'Sync APIs (REST / SOAP / OData)', pl: 'Sync API (REST / SOAP / OData)' },
    'events_async': { en: 'Events / messaging / webhooks', pl: 'Eventy / messaging / webhooki' },
    'analytics_batch': { en: 'Analytics / warehouse / lake pipelines', pl: 'Analityka / warehouse / lake' },
    'mixed_unknown': { en: 'Not sure / mixed — need workshop', pl: 'Nie wiem / mieszane — potrzebny warsztat' },
    'greenfield_none': { en: 'Greenfield — no estate yet', pl: 'Greenfield — jeszcze bez estate' },
  },
  'internal_style': {
    'admin_console': { en: 'Ops / admin console', pl: 'Panel operacyjny / admina' },
    'business_app': { en: 'Business app / portal', pl: 'Aplikacja biznesowa / portal' },
    'lowcode': { en: 'Citizen-dev / low-code platform', pl: 'Platforma low-code / citizen-dev' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
    'workflow': { en: 'Long-running workflows', pl: 'Długotrwałe procesy' },
  },
  'iot_style': {
    'device_lifecycle': { en: 'Device lifecycle & OTA', pl: 'Lifecycle urządzeń i OTA' },
    'fleet_twin': { en: 'Fleet telematics & digital twin', pl: 'Telematyka floty i digital twin' },
    'industrial_edge': { en: 'Industrial edge control', pl: 'Sterowanie industrial edge' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
    'telemetry': { en: 'Telemetry & alerts', pl: 'Telemetria i alerty' },
  },
  'iot_placement': {
    'cloud_first': { en: 'Cloud-first ingest', pl: 'Ingest najpierw w chmurze' },
    'edge_heavy': { en: 'Edge-heavy / OT local', pl: 'Silny edge / lokalne OT' },
    'hybrid': { en: 'Hybrid cloud + edge', pl: 'Hybryda chmura + edge' },
  },
  'locked_source': {
    'data_warehouse': { en: 'Data warehouse / analytics', pl: 'Hurtownia danych / analityka' },
    'erp_crm': { en: 'ERP / CRM', pl: 'ERP / CRM' },
    'identity_provider': { en: 'Corporate identity / SSO', pl: 'Tożsamość korporacyjna / SSO' },
    'legacy_onprem': { en: 'Legacy on-prem systems', pl: 'Systemy legacy on-prem' },
    'payments': { en: 'Payments / billing', pl: 'Płatności / billing' },
    'work_management': { en: 'Work management / collaboration', pl: 'Work management / współpraca' },
  },
  'locked_target': {
    'data_warehouse': { en: 'Data warehouse / analytics', pl: 'Hurtownia danych / analityka' },
    'erp_crm': { en: 'ERP / CRM', pl: 'ERP / CRM' },
    'identity_provider': { en: 'Corporate identity / SSO', pl: 'Tożsamość korporacyjna / SSO' },
    'legacy_onprem': { en: 'Legacy on-prem systems', pl: 'Systemy legacy on-prem' },
    'payments': { en: 'Payments / billing', pl: 'Płatności / billing' },
    'work_management': { en: 'Work management / collaboration', pl: 'Work management / współpraca' },
  },
  'marketing_style': {
    'cms': { en: 'Editorial CMS', pl: 'CMS editorialny' },
    'headless': { en: 'Headless storefront + content', pl: 'Sklep headless i treści' },
    'jamstack': { en: 'Jamstack marketing site', pl: 'Strona marketingowa Jamstack' },
    'secure_web': { en: 'Secure enterprise web', pl: 'Bezpieczny web enterprise' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'migration_approach': {
    'mixed_6r': { en: 'Mixed 6R / undecided', pl: 'Mieszane 6R / nie zdecydowane' },
    'refactor': { en: 'Refactor / strangler', pl: 'Refactor / strangler' },
    'rehost': { en: 'Rehost (lift & shift)', pl: 'Przeniesienie bez przebudowy' },
    'replace': { en: 'Replace with new build', pl: 'Zastąpienie nową budową' },
    'replatform': { en: 'Replatform (light changes)', pl: 'Replatform (lekkie zmiany)' },
  },
  'migration_from': {
    'legacy_soa': { en: 'Legacy SOA / ESB', pl: 'Legacy SOA / ESB' },
    'mixed_estate': { en: 'Mixed estate (many systems)', pl: 'Mieszany park (wiele systemów)' },
    'monolith_onprem': { en: 'On-prem monolith', pl: 'Monolit on-prem' },
    'single_cloud': { en: 'Single-cloud lift candidate', pl: 'Kandydat do liftu w jednej chmurze' },
    'unknown_from': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'migration_style': {
    'anti_corruption': { en: 'Anti-corruption layer', pl: 'Warstwa oddzielająca stary kontrakt' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
    'strangler': { en: 'Strangler cutover', pl: 'Cutover strangler' },
  },
  'mobile_style': {
    'bff': { en: 'Mobile + BFF (always-online)', pl: 'Aplikacja mobilna z dedykowanym backendem' },
    'offline': { en: 'Offline-first / field', pl: 'Tryb offline / praca w terenie' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
    'super_app': { en: 'Super-app / modular shell', pl: 'Super-aplikacja z modułami' },
  },
  'optimize_baseline': {
    'cloud_prod': { en: 'Production in public cloud', pl: 'Produkcja w chmurze publicznej' },
    'microservices': { en: 'Microservices / many services', pl: 'Mikroserwisy / wiele serwisów' },
    'monolith': { en: 'Monolith deployment', pl: 'Wdrożenie monolityczne' },
    'no_docs': { en: 'Limited docs / tribal knowledge', pl: 'Mało dokumentacji / wiedza w zespole' },
    'onprem_prod': { en: 'Production on-prem / private', pl: 'Produkcja on-prem / prywatna' },
    'unknown_baseline': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'optimize_focus': {
    'cost': { en: 'Cloud / run cost', pl: 'Koszt chmury / run' },
    'delivery': { en: 'Delivery process', pl: 'Proces dostarczania' },
    'performance': { en: 'Performance & latency', pl: 'Wydajność i opóźnienia' },
    'security': { en: 'Security & compliance', pl: 'Bezpieczeństwo i zgodność' },
  },
  'pack_audience': {
    'consultancy_delivery': { en: 'Client / pre-sales delivery (SI)', pl: 'Dostawa dla klienta / pre-sales (SI)' },
    'product_org': { en: 'Our product / engineering org', pl: 'Nasz produkt / organizacja inżynierska' },
  },
  'peak_load_shape': {
    'flash_sale': { en: 'Flash sale / promo spike', pl: 'Flash sale / skok promocji' },
    'launch_window': { en: 'Hard launch / go-live window', pl: 'Twardy launch / okno go-live' },
    'sustained_growth': { en: 'Sustained growth ramp', pl: 'Trwały wzrost' },
    'unknown_peak': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'platform_change_type': {
    'extend': { en: 'Extend an existing flow', pl: 'Rozszerz istniejący przepływ' },
    'expose': { en: 'Expose via API / event', pl: 'Udostępnij przez API / zdarzenie' },
    'integrate': { en: 'Integrate systems', pl: 'Zintegruj systemy' },
    'sync': { en: 'Keep records in sync', pl: 'Utrzymuj rekordy zsynchronizowane' },
  },
  'platform_maturity': {
    'basic_cicd': { en: 'Basic CI/CD + manual releases', pl: 'Podstawowe CI/CD + ręczne release' },
    'greenfield_ops': { en: 'Greenfield — little or no CI/CD yet', pl: 'Greenfield — mało lub brak CI/CD' },
    'large': { en: 'Large — tens of thousands+ / day', pl: 'Duża — dziesiątki tysięcy+ / dzień' },
    'mature_gitops': { en: 'Mature GitOps / paved roads', pl: 'Dojrzały GitOps / paved roads' },
    'medium': { en: 'Medium — thousands / day', pl: 'Średnia — tysiące / dzień' },
    'small': { en: 'Small — hundreds / day', pl: 'Mała — setki / dzień' },
    'unknown_platform': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
    'unknown_scale': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  },
  'platform_profile_preset': {
    'custom': { en: 'Custom estate', pl: 'Własny estate' },
    'erp_crm_orders': { en: 'ERP ↔ CRM orders (gateway + broker)', pl: 'ERP ↔ CRM zamówienia (gateway + broker)' },
  },
  'priority_nfr': {
    'cost': { en: 'Low cost / predictable spend', pl: 'Niski / przewidywalny koszt' },
    'latency': { en: 'Low latency / realtime UX', pl: 'Niskie opóźnienia / interfejs na żywo' },
    'observability': { en: 'Observability / operations', pl: 'Observability / operacje' },
    'reliability': { en: 'Reliability & uptime', pl: 'Niezawodność i uptime' },
    'scalability': { en: 'Scalability for growth', pl: 'Skalowalność na wzrost' },
    'security': { en: 'Security & compliance', pl: 'Bezpieczeństwo i zgodność' },
    'time_to_market': { en: 'Time to market', pl: 'Czas wejścia na rynek' },
  },
  'product_type': {
    'ai': { en: 'AI / ML product', pl: 'Produkt AI / ML' },
    'api_platform': { en: 'HTTP APIs and API management', pl: 'API HTTP i zarządzanie API' },
    'big_compute': { en: 'Big compute / HPC', pl: 'Duży compute / HPC' },
    'cms': { en: 'Editorial CMS', pl: 'System do publikacji treści' },
    'customer_portal': { en: 'Customer self-service portal', pl: 'Portal samoobsługowy klienta' },
    'data': { en: 'Data / analytics / ETL', pl: 'Dane / analityka / ETL' },
    'data_ai': { en: 'Data & AI', pl: 'Dane i sztuczna inteligencja' },
    'distributed': { en: 'Advanced distributed setup', pl: 'Zaawansowana infrastruktura rozproszona' },
    'ecommerce': { en: 'Online store or marketplace', pl: 'Sklep internetowy lub marketplace' },
    'edge': { en: 'Edge compute', pl: 'Obliczenia brzegowe' },
    'enterprise_web': { en: 'Secure enterprise web', pl: 'Bezpieczna aplikacja dla firm' },
    'experimentation': { en: 'Feature flags / experiments', pl: 'Feature flagi / eksperymenty' },
    'hybrid': { en: 'Hybrid / multi-cloud connectivity', pl: 'Łączność chmury hybrydowej / wielochmurowej' },
    'identity': { en: 'Identity / CIAM', pl: 'Logowanie i tożsamość użytkowników' },
    'idp': { en: 'Internal developer platform', pl: 'Platforma dla deweloperów (IDP)' },
    'integration': { en: 'Integration / messaging', pl: 'Integracja systemów / komunikacja' },
    'internal': { en: 'Internal / back-office', pl: 'System wewnętrzny / back-office' },
    'iot': { en: 'IoT / connected devices', pl: 'IoT / urządzenia' },
    'marketing': { en: 'Marketing / content site', pl: 'Strona marketingowa / treści' },
    'media': { en: 'Media streaming', pl: 'Streaming mediów' },
    'migration': { en: 'Migration / modernization', pl: 'Migracja / modernizacja' },
    'mobile': { en: 'Mobile product', pl: 'Aplikacja mobilna' },
    'mvp': { en: 'MVP / early product', pl: 'MVP / wczesny produkt' },
    'n_tier': { en: 'Classic layered web app', pl: 'Klasyczna warstwowa aplikacja web' },
    'notifications': { en: 'Notifications / engagement', pl: 'Powiadomienia dla użytkowników' },
    'observability': { en: 'Observability platform', pl: 'Platforma obserwowalności' },
    'platforms': { en: 'Platforms & backends', pl: 'Platformy i zaplecze techniczne' },
    'products': { en: 'Apps & products', pl: 'Aplikacje i produkty' },
    'pwa': { en: 'Progressive Web App', pl: 'Aplikacja webowa (PWA)' },
    'realtime': { en: 'Realtime collaboration', pl: 'Współpraca na żywo' },
    'saas': { en: 'SaaS product', pl: 'Aplikacja SaaS w chmurze' },
    'search': { en: 'Enterprise search', pl: 'Wyszukiwanie w firmie' },
    'transform': { en: 'Migration & fabric', pl: 'Migracja i modernizacja' },
  },
  'process_change': {
    'publish_events': { en: 'Publish a status when something happens', pl: 'Publikuj status, gdy coś się wydarzy' },
    'replace_handoff': { en: 'Replace a manual handoff', pl: 'Zastąp ręcznie przekazywany krok' },
    'sync_records': { en: 'Keep the same records in sync', pl: 'Utrzymuj te same rekordy zsynchronizowane' },
  },
  'process_domain': {
    'customer': { en: 'Customer operations', pl: 'Obsługa klientów' },
    'finance': { en: 'Finance', pl: 'Finanse' },
    'hr': { en: 'People / HR', pl: 'Kadry / HR' },
    'procurement': { en: 'Procurement', pl: 'Zakupy' },
  },
  'rfp_brief': {

  },
  'saas_style': {
    'b2b_workspace': { en: 'B2B orgs & workspaces', pl: 'Organizacje B2B i przestrzenie robocze' },
    'pooled': { en: 'Pooled multi-tenant', pl: 'Wspólna infrastruktura dla wielu klientów' },
    'silo': { en: 'Silo / dedicated tenant', pl: 'Dedykowana infrastruktura na klienta' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
    'usage_metering': { en: 'Usage-metered billing', pl: 'Rozliczanie za zużycie' },
  },
  'scale': {
    'large': { en: 'Large — tens of thousands+', pl: 'Duża — dziesiątki tysięcy+' },
    'medium': { en: 'Medium — thousands', pl: 'Średnia — tysiące' },
    'small': { en: 'Small — hundreds of users / devices', pl: 'Mała — setki użytkowników / urządzeń' },
    'unknown_scale': { en: 'I don’t know yet', pl: 'Jeszcze nie wiem' },
  },
  'search_style': {
    'enterprise': { en: 'Enterprise find (ACL)', pl: 'Wyszukiwanie enterprise (ACL)' },
    'media': { en: 'Media / content discovery', pl: 'Odkrywanie mediów / treści' },
    'rag': { en: 'Grounded Q&A / RAG', pl: 'Q&A na dokumentach / RAG' },
    'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  },
  'stage': {
    'growth': { en: 'Growing product', pl: 'Rosnący produkt' },
    'idea': { en: 'Idea / early exploration', pl: 'Pomysł / wczesna eksploracja' },
    'migration': { en: 'Migration / modernization', pl: 'Migracja / modernizacja' },
    'mvp_build': { en: 'Building MVP', pl: 'Budowa MVP' },
  },
  'users': {
    'b2b': { en: 'Business users (B2B)', pl: 'Użytkownicy biznesowi (B2B)' },
    'b2c': { en: 'Consumers (B2C)', pl: 'Konsumenci (B2C)' },
    'developers': { en: 'Developers / platform teams', pl: 'Deweloperzy / zespoły platformowe' },
    'internal_staff': { en: 'Internal employees', pl: 'Pracownicy wewnętrzni' },
    'mixed': { en: 'Mixed / marketplace', pl: 'Mieszani użytkownicy / marketplace' },
  },
};

const FLAT_ANSWER_LABELS = {
  'admin_backoffice': { en: 'Admin / back-office', pl: 'Admin / back-office' },
  'agents_tools': { en: 'Agents & tool use', pl: 'Agenci i narzędzia' },
  'ai': { en: 'AI / ML product', pl: 'Produkt AI / ML' },
  'alerting': { en: 'Rules & alerting', pl: 'Reguły i alerty' },
  'anti_corruption': { en: 'Anti-corruption layer', pl: 'Warstwa oddzielająca stary kontrakt' },
  'api_auth': { en: 'API authentication', pl: 'Uwierzytelnianie API' },
  'api_connectors': { en: 'Sync API connectors', pl: 'Konektory API sync' },
  'api_facade': { en: 'API facade / routing', pl: 'Fasada API / routing' },
  'api_platform': { en: 'HTTP APIs and API management', pl: 'API HTTP i zarządzanie API' },
  'api_versioning': { en: 'Versioning & lifecycle', pl: 'Wersjonowanie i lifecycle' },
  'apim': { en: 'API Management product', pl: 'Produkt API Management' },
  'approvals': { en: 'Approvals & routing', pl: 'Akceptacje i routing' },
  'async_queue': { en: 'Async queue workers', pl: 'Async queue workers' },
  'async_webhooks': { en: 'Async / webhooks', pl: 'Async / webhooki' },
  'audit_compliance': { en: 'Audit & compliance trail', pl: 'Audyt i ślad compliance' },
  'b2b': { en: 'Business users (B2B)', pl: 'Użytkownicy biznesowi (B2B)' },
  'b2b_workspace': { en: 'B2B orgs & workspaces', pl: 'Organizacje B2B i przestrzenie robocze' },
  'b2c': { en: 'Consumers (B2C)', pl: 'Konsumenci (B2C)' },
  'basic_cicd': { en: 'Basic CI/CD + manual releases', pl: 'Podstawowe CI/CD + ręczne release' },
  'batch': { en: 'Batch / nearline ML', pl: 'ML batch / nearline' },
  'batch_jobs': { en: 'Batch / nightly jobs', pl: 'Batch / joby nocne' },
  'batch_only': { en: 'Mostly offline / batch', pl: 'Głównie offline / batch' },
  'bff': { en: 'Mobile + BFF (always-online)', pl: 'Aplikacja mobilna z dedykowanym backendem' },
  'bi_reporting': { en: 'BI & self-serve analytics', pl: 'BI i analityka self-serve' },
  'big_compute': { en: 'Big compute / HPC', pl: 'Duży compute / HPC' },
  'billing_subscriptions': { en: 'Billing & subscriptions', pl: 'Billing i subskrypcje' },
  'budget_gate': { en: 'Budget / funding gate', pl: 'Budżet / bramka finansowania' },
  'business_app': { en: 'Business app / portal', pl: 'Aplikacja biznesowa / portal' },
  'cart_checkout': { en: 'Cart & checkout', pl: 'Koszyk i checkout' },
  'cdc': { en: 'CDC / streaming platform', pl: 'Platforma CDC / streaming' },
  'chargeback': { en: 'Org chargeback / cost center', pl: 'Chargeback / cost center' },
  'cloud_aws': { en: 'Preferred cloud: AWS', pl: 'Preferowana chmura: AWS' },
  'cloud_azure': { en: 'Preferred cloud: Azure', pl: 'Preferowana chmura: Azure' },
  'cloud_gcp': { en: 'Preferred cloud: GCP', pl: 'Preferowana chmura: GCP' },
  'cloud_oracle': { en: 'Preferred cloud: Oracle / OCI', pl: 'Preferowana chmura: Oracle / OCI' },
  'cloud_prod': { en: 'Production in public cloud', pl: 'Produkcja w chmurze publicznej' },
  'cms': { en: 'Editorial CMS', pl: 'System do publikacji treści' },
  'consultancy_delivery': { en: 'Client / pre-sales delivery (SI)', pl: 'Dostawa dla klienta / pre-sales (SI)' },
  'copilot': { en: 'In-product copilot', pl: 'Copilot w produkcie' },
  'core_workflows': { en: 'Core user workflows', pl: 'Główne ścieżki użytkownika' },
  'corpus_large': { en: 'Large — 500k+', pl: 'Duży — 500k+' },
  'corpus_medium': { en: 'Medium — ~10k–500k', pl: 'Średni — ~10k–500k' },
  'corpus_small': { en: 'Small — up to ~10k docs / chunks', pl: 'Mały — do ~10k dokumentów / chunków' },
  'cqrs': { en: 'CQRS + event-sourced core', pl: 'CQRS + event sourcing' },
  'customer_portal': { en: 'Customer self-service portal', pl: 'Portal samoobsługowy klienta' },
  'data': { en: 'Data / analytics / ETL', pl: 'Dane / analityka / ETL' },
  'data_ai': { en: 'Data & AI', pl: 'Dane i sztuczna inteligencja' },
  'data_governance': { en: 'Data governance', pl: 'Governance danych' },
  'data_replication': { en: 'Data replication & sync', pl: 'Replikacja i sync danych' },
  'data_residency': { en: 'Data residency / sovereignty', pl: 'Lokalizacja i suwerenność danych' },
  'data_warehouse': { en: 'Data warehouse / analytics', pl: 'Hurtownia danych / analityka' },
  'decommission': { en: 'Legacy decommission', pl: 'Wycofanie legacy' },
  'dedicated_endpoint': { en: 'Dedicated inference endpoint', pl: 'Dedykowany endpoint inferencji' },
  'delivery': { en: 'Delivery process', pl: 'Proces dostarczania' },
  'delivery_deadline': { en: 'Hard delivery deadline / promo go-live', pl: 'Twardy deadline / go-live promocji' },
  'locked_data_estate': { en: 'Existing data platforms stay (warehouse / lake / streaming)', pl: 'Istniejące platformy danych zostają (hurtownia / lake / streaming)' },
  'developer_portal': { en: 'Developer portal', pl: 'Portal deweloperski' },
  'developers': { en: 'Developers / platform teams', pl: 'Deweloperzy / zespoły platformowe' },
  'device_lifecycle': { en: 'Device lifecycle & OTA', pl: 'Lifecycle urządzeń i OTA' },
  'device_management': { en: 'Device management', pl: 'Zarządzanie urządzeniami' },
  'device_telemetry': { en: 'Device telemetry', pl: 'Telemetria urządzeń' },
  'distributed': { en: 'Advanced distributed setup', pl: 'Zaawansowana infrastruktura rozproszona' },
  'documents': { en: 'Documents & attachments', pl: 'Dokumenty i załączniki' },
  'dual_run_validation': { en: 'Dual-run validation', pl: 'Walidacja dual-run' },
  'ecommerce': { en: 'Online store or marketplace', pl: 'Sklep internetowy lub marketplace' },
  'edge_processing': { en: 'Edge processing', pl: 'Przetwarzanie edge' },
  'enterprise': { en: 'Enterprise find (ACL)', pl: 'Wyszukiwanie enterprise (ACL)' },
  'enterprise_web': { en: 'Secure enterprise web', pl: 'Bezpieczna aplikacja dla firm' },
  'erp_crm': { en: 'ERP / CRM (SAP, Oracle, Dynamics…)', pl: 'ERP / CRM (SAP, Oracle, Dynamics…)' },
  'etl_batch': { en: 'ETL & batch jobs', pl: 'ETL i joby batch' },
  'eu_ai_act': { en: 'EU AI Act in scope', pl: 'Akt o AI UE w zakresie' },
  'eval_monitoring': { en: 'Eval & monitoring', pl: 'Ewaluacja i monitoring' },
  'event_hub': { en: 'Event-driven integration hub', pl: 'Event-driven hub integracyjny' },
  'event_routing': { en: 'Event routing', pl: 'Routing zdarzeń' },
  'experimentation': { en: 'Feature flags / experiments', pl: 'Feature flagi / eksperymenty' },
  'external_bank': { en: 'External memory bank / RAG', pl: 'Zewnętrzny bank pamięci / RAG' },
  'external_integrations': { en: 'External integrations', pl: 'Integracje zewnętrzne' },
  'find_data': { en: 'Search & analytics', pl: 'Wyszukiwanie i analityka' },
  'forms_crud': { en: 'Forms & CRUD workflows', pl: 'Formularze i CRUD' },
  'full_policy': { en: 'Full policy (prompt + tools + egress)', pl: 'Pełna polityka (prompt + tooli + egress)' },
  'gdpr': { en: 'GDPR / personal data', pl: 'RODO / dane osobowe' },
  'genai': { en: 'Generative / assistants', pl: 'Generatywne / asystenci' },
  'graphql': { en: 'GraphQL federation', pl: 'GraphQL federation' },
  'greenfield_ops': { en: 'Greenfield — little or no CI/CD yet', pl: 'Greenfield — mało lub brak CI/CD' },
  'growth': { en: 'Growing product', pl: 'Rosnący produkt' },
  'guaranteed_delivery': { en: 'Guaranteed delivery', pl: 'Gwarantowane dostarczenie' },
  'guardrails': { en: 'Guardrails & safety', pl: 'Guardrails i bezpieczeństwo' },
  'heavy': { en: 'Heavy — tens of thousands+ / day', pl: 'Ciężki — dziesiątki tysięcy+ / dzień' },
  'human_review': { en: 'Human review loop', pl: 'Pętla human review' },
  'idea': { en: 'Idea / early exploration', pl: 'Pomysł / wczesna eksploracja' },
  'identity': { en: 'Identity / CIAM', pl: 'Logowanie i tożsamość użytkowników' },
  'identity_cutover': { en: 'Identity / SSO cutover', pl: 'Cutover tożsamości / SSO' },
  'identity_provider': { en: 'Corporate identity / SSO', pl: 'Tożsamość korporacyjna / SSO' },
  'idp': { en: 'Internal developer platform', pl: 'Platforma dla deweloperów (IDP)' },
  'industrial_edge': { en: 'Industrial edge control', pl: 'Sterowanie industrial edge' },
  'ingestion_etl': { en: 'Ingestion & ETL', pl: 'Ingestia i ETL' },
  'integration': { en: 'Integration / messaging', pl: 'Integracja systemów / komunikacja' },
  'integration_ops': { en: 'Integration observability', pl: 'Integration observability' },
  'internal': { en: 'Internal / back-office', pl: 'System wewnętrzny / back-office' },
  'internal_staff': { en: 'Internal employees', pl: 'Pracownicy wewnętrzni' },
  'inventory_discovery': { en: 'Estate inventory', pl: 'Inwentaryzacja parku' },
  'iot': { en: 'IoT / connected devices', pl: 'IoT / urządzenia' },
  'jamstack': { en: 'Jamstack marketing site', pl: 'Strona marketingowa Jamstack' },
  'knowledge_ingestion': { en: 'Knowledge ingestion', pl: 'Ingestia wiedzy' },
  'lakehouse': { en: 'Lakehouse (open tables + SQL)', pl: 'Lakehouse (otwarte tabele + SQL)' },
  'latency': { en: 'Low latency / realtime UX', pl: 'Niskie opóźnienia / interfejs na żywo' },
  'legacy_onprem': { en: 'Legacy on-prem systems', pl: 'Legacy on-prem' },
  'legacy_soa': { en: 'Legacy SOA / ESB', pl: 'Legacy SOA / ESB' },
  'long_term': { en: 'Long-term user / org', pl: 'Long-term użytkownik / org' },
  'managed_api': { en: 'Managed LLM APIs (usage-based)', pl: 'Zarządzane API LLM (pay-per-use)' },
  'marketing': { en: 'Marketing / content site', pl: 'Strona marketingowa / treści' },
  'marketplace': { en: 'Two-sided marketplace', pl: 'Marketplace dwustronny' },
  'mature_gitops': { en: 'Mature GitOps / paved roads', pl: 'Dojrzały GitOps / paved roads' },
  'mesh': { en: 'Data mesh (domain products)', pl: 'Data mesh (produkty domenowe)' },
  'messaging_bus': { en: 'Enterprise messaging bus', pl: 'Firmowa szyna komunikatów' },
  'microservices': { en: 'Microservices / many services', pl: 'Mikroserwisy / wiele serwisów' },
  'migration': { en: 'Migration / modernization', pl: 'Migracja / modernizacja' },
  'mixed': { en: 'Mixed / marketplace', pl: 'Mieszani użytkownicy / marketplace' },
  'mixed_6r': { en: 'Mixed 6R / undecided', pl: 'Mieszane 6R / nie zdecydowane' },
  'mixed_estate': { en: 'Mixed estate (many systems)', pl: 'Mieszany park (wiele systemów)' },
  'ml_features': { en: 'ML feature serving', pl: 'Feature serving ML' },
  'ml_online': { en: 'Online & batch ML', pl: 'ML online i batch' },
  'mobile': { en: 'Mobile product', pl: 'Aplikacja mobilna' },
  'mobile_backend': { en: 'Mobile backend / BFF', pl: 'Backend mobilny / BFF' },
  'mobile_ui': { en: 'Mobile client UX', pl: 'UX klienta mobilnego' },
  'monolith': { en: 'Monolith deployment', pl: 'Wdrożenie monolityczne' },
  'monolith_onprem': { en: 'On-prem monolith', pl: 'Monolit on-prem' },
  'multi_region': { en: 'Multi-region active-active', pl: 'Multi-region active-active' },
  'mvp': { en: 'MVP / early product', pl: 'MVP / wczesny produkt' },
  'mvp_build': { en: 'Building MVP', pl: 'Budowa MVP' },
  'n_tier': { en: 'Classic layered web app', pl: 'Klasyczna warstwowa aplikacja web' },
  'no_docs': { en: 'Limited docs / tribal knowledge', pl: 'Mało dokumentacji / wiedza w zespole' },
  'offline': { en: 'Offline-first / field', pl: 'Tryb offline / praca w terenie' },
  'offline_sync': { en: 'Offline & sync', pl: 'Offline i synchronizacja' },
  'on_prem': { en: 'On-prem / private cloud required', pl: 'Wymagane on-premise lub prywatna chmura' },
  'onprem_prod': { en: 'Production on-prem / private', pl: 'Produkcja on-prem / prywatna' },
  'order_fulfillment': { en: 'Orders & fulfillment', pl: 'Zamówienia i fulfillment' },
  'per_agent': { en: 'Per-agent / specialty split', pl: 'Podział per agent / specjalizacja' },
  'per_run': { en: 'Per-run totals', pl: 'Sumy per-run' },
  'performance': { en: 'Performance & latency', pl: 'Wydajność i opóźnienia' },
  'pipeline': { en: 'Central analytics pipeline', pl: 'Centralny pipeline analityczny' },
  'pipeline_ml': { en: 'Batch / offline ML (cost focus)', pl: 'Batch / offline ML (koszty)' },
  'platforms': { en: 'Platforms & backends', pl: 'Platformy i zaplecze techniczne' },
  'pooled': { en: 'Pooled multi-tenant', pl: 'Wspólna infrastruktura dla wielu klientów' },
  'product': { en: 'Product feature — thousands+ / day', pl: 'Feature produktu — tysiące+ / dzień' },
  'product_api': { en: 'Product API for integrators', pl: 'API produktu dla integratorów' },
  'product_catalog': { en: 'Product catalog & search', pl: 'Katalog i wyszukiwanie' },
  'product_org': { en: 'Our product / engineering org', pl: 'Nasz produkt / organizacja inżynierska' },
  'production': { en: 'Broad production', pl: 'Szeroka produkcja' },
  'products': { en: 'Apps & products', pl: 'Aplikacje i produkty' },
  'promotions': { en: 'Promotions & loyalty', pl: 'Promocje i lojalność' },
  'prompt_only': { en: 'Prompt / output filters', pl: 'Filtry prompt / output' },
  'push_notifications': { en: 'Push notifications', pl: 'Powiadomienia push' },
  'pwa': { en: 'Progressive Web App', pl: 'Aplikacja webowa (PWA)' },
  'quote': { en: 'Quote / estimate advisor', pl: 'Doradca wyceny / estimate' },
  'rate_limits': { en: 'Rate limits & quotas', pl: 'Limity i quota' },
  'rbac': { en: 'Roles & permissions', pl: 'Role i uprawnienia' },
  'realtime': { en: 'Realtime collaboration', pl: 'Współpraca na żywo' },
  'refactor': { en: 'Refactor / strangler', pl: 'Refactor / strangler' },
  'rehost': { en: 'Rehost (lift & shift)', pl: 'Przeniesienie bez przebudowy' },
  'reliability': { en: 'Reliability & uptime', pl: 'Niezawodność i uptime' },
  'replace': { en: 'Replace with new build', pl: 'Zastąpienie nową budową' },
  'replatform': { en: 'Replatform (light changes)', pl: 'Replatform (lekkie zmiany)' },
  'reporting': { en: 'Dashboards & reporting', pl: 'Dashboardy i raporty' },
  'reporting_insights': { en: 'Reporting / insights', pl: 'Raportowanie / insighty' },
  'retrieval_rag': { en: 'Retrieval / RAG', pl: 'Retrieval / RAG' },
  'saas': { en: 'SaaS product', pl: 'Aplikacja SaaS w chmurze' },
  'scalability': { en: 'Scalability for growth', pl: 'Skalowalność na wzrost' },
  'scope_tbd': { en: 'Still deciding / TBD', pl: 'Jeszcze ustalamy / TBD' },
  'secure_web': { en: 'Secure enterprise web', pl: 'Bezpieczny web enterprise' },
  'security': { en: 'Security & compliance', pl: 'Bezpieczeństwo i zgodność' },
  'self_hosted': { en: 'Self-hosted models / GPUs', pl: 'Modele self-hosted / GPU' },
  'seller_portal': { en: 'Seller / vendor portal', pl: 'Portal sprzedawcy' },
  'serverless': { en: 'Serverless / HTTP APIs', pl: 'Serverless / HTTP API' },
  'service_mesh': { en: 'Zero-trust service mesh', pl: 'Service mesh zero-trust' },
  'session': { en: 'Session-only', pl: 'Tylko sesja' },
  'short_term': { en: 'Short-term run / thread', pl: 'Short-term run / wątek' },
  'silo': { en: 'Silo / dedicated tenant', pl: 'Dedykowana infrastruktura na klienta' },
  'single_cloud': { en: 'Single-cloud lift candidate', pl: 'Kandydat do liftu w jednej chmurze' },
  'soap_odata': { en: 'SOAP / OData (ERP, CRM, Microsoft)', pl: 'SOAP / OData (ERP, CRM, Microsoft)' },
  'soc2': { en: 'SOC 2 controls expected', pl: 'Oczekiwane kontrole SOC 2' },
  'standard': { en: 'Standard / typical for this type', pl: 'Typowy / standardowy dla tego typu' },
  'strangler': { en: 'Strangler cutover', pl: 'Cutover strangler' },
  'streaming_cdc': { en: 'Streaming / CDC', pl: 'Streaming / CDC' },
  'super_app': { en: 'Super-app / modular shell', pl: 'Super-aplikacja z modułami' },
  'team_size': { en: 'Team size / hiring plan', pl: 'Wielkość zespołu / hiring' },
  'team_small': { en: 'Very small team (1–3 people)', pl: 'Bardzo mały zespół (1–3 osoby)' },
  'telemetry': { en: 'Telemetry & alerts', pl: 'Telemetria i alerty' },
  'tenant_workspace': { en: 'Tenants / workspaces', pl: 'Tenanty / workspace' },
  'time_to_market': { en: 'Time to market', pl: 'Czas wejścia na rynek' },
  'timeline': { en: 'Timeline / go-live pressure', pl: 'Termin / presja go-live' },
  'tool_sandbox': { en: 'Tool sandbox + allow-list', pl: 'Sandbox tooli + allow-list' },
  'transactional': { en: 'Transactional web', pl: 'Web transakcyjny' },
  'transform': { en: 'Migration & fabric', pl: 'Migracja i modernizacja' },
  'transformations': { en: 'Transformations & mapping', pl: 'Transformacje i mapowanie' },
  'unknown_baseline': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  'unknown_corpus': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  'unknown_from': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  'unknown_integration': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  'unknown_platform': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  'unknown_volume': { en: 'Not sure yet', pl: 'Jeszcze nie wiem' },
  'usage_metering': { en: 'Usage-metered billing', pl: 'Rozliczanie za zużycie' },
  'user_accounts': { en: 'User accounts', pl: 'Konta użytkowników' },
  'user_auth': { en: 'User sign-up & login', pl: 'Rejestracja i logowanie' },
  'vendor_compare': { en: 'Build vs buy / vendor compare', pl: 'Build vs buy / porównanie vendorów' },
  'warehouse_model': { en: 'Warehouse / lake modeling', pl: 'Modelowanie warehouse / lake' },
  'workflow': { en: 'Long-running workflows', pl: 'Długotrwałe procesy' },
};

const UNKNOWN_FIELD_LABELS = {
  scale: { en: 'scale', pl: 'skala' },
  users: { en: 'audience', pl: 'odbiorcy' },
  constraints: { en: 'constraints', pl: 'ograniczenia' },
  priority_nfr: { en: 'priorities', pl: 'priorytety' },
  ai_volume: { en: 'AI volume', pl: 'wolumen AI' },
  ai_workload: { en: 'AI workload', pl: 'obciążenie AI' },
  ai_serving: { en: 'model serving', pl: 'serwowanie modelu' },
  ai_data_scale: { en: 'knowledge corpus size', pl: 'rozmiar korpusu wiedzy' },
  product_subtype: { en: 'product shape', pl: 'kształt produktu' },
  integration_landscape: { en: 'integrations', pl: 'integracje' },
  transfer_plane: { en: 'how systems connect', pl: 'jak systemy się łączą' },
  capability_scope: { en: 'first-release scope', pl: 'zakres pierwszego wydania' },
  optimize_baseline: { en: 'current baseline', pl: 'obecny baseline' },
  optimize_focus: { en: 'optimization focus', pl: 'punkt optymalizacji' },
  business_outcome: { en: 'business outcome', pl: 'wynik biznesowy' },
  success_signal: { en: 'success signal', pl: 'sygnał sukcesu' },
  peak_load_shape: { en: 'peak load shape', pl: 'kształt szczytu obciążenia' },
  iot_placement: { en: 'IoT placement', pl: 'lokalizacja IoT' },
  platform_profile_preset: { en: 'platform estate', pl: 'estate platformy' },
  platform_change_type: { en: 'platform change type', pl: 'rodzaj zmiany platformy' },
  process_change: { en: 'process change', pl: 'zmiana procesu' },
  process_domain: { en: 'business area', pl: 'obszar biznesowy' },
  platform_maturity: { en: 'delivery maturity', pl: 'dojrzałość dostarczania' },
  migration_from: { en: 'migration source', pl: 'źródło migracji' },
  migration_style: { en: 'migration shape', pl: 'kształt migracji' },
  migration_approach: { en: 'migration approach', pl: 'podejście migracji' },
  rfp_product_shape: { en: 'product shape from the brief', pl: 'kształt produktu z briefu' },
  rfp_brief_incomplete: { en: 'brief is still thin', pl: 'brief jest jeszcze ubogi' },
  rfp_brief_requirements: { en: 'requirements in the brief', pl: 'wymagania w briefie' },
  rfp_brief_audience: { en: 'audience in the brief', pl: 'odbiorcy w briefie' },
  rfp_services_sow: { en: 'services scope of work', pl: 'zakres usług' },
  rfp_integration_sow: { en: 'integration scope of work', pl: 'zakres integracji' },
  legacy_integration: { en: 'legacy integrations', pl: 'integracje z legacy' },
  agent_gov_tier: { en: 'agent governance tier', pl: 'poziom governance agentów' },
};

const CONFIDENCE_LABELS = {
  HIGH: { en: 'High', pl: 'Wysoka' },
  MEDIUM: { en: 'Medium', pl: 'Średnia' },
  LOW: { en: 'Low', pl: 'Niska' },
};

function appLang(lang) {
  return String(lang || 'en').toLowerCase().startsWith('pl') ? 'pl' : 'en';
}

function proseCase(label) {
  const s = String(label || '').trim();
  if (!s) return '';
  if (/^(?:AI|API|IoT|MVP|RAG|ETL|CDC|GDPR|SOC|EU|B2B|B2C|CI|ML|SSO|RBAC|BI|KPI)\b/.test(s)) return s;
  if (/^I\b/.test(s)) return s;
  if (/^[A-Z]{2,}\b/.test(s)) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

function rowText(row, lang) {
  if (!row) return '';
  const code = appLang(lang);
  return String((code === 'pl' ? row.pl || row.en : row.en || row.pl) || '').trim();
}

/**
 * Human label for one stored answer id.
 * Pass questionId when the same id means different things (cost, large, none).
 * @param {string|null|undefined} id
 * @param {'en'|'pl'|string} [lang]
 * @param {string} [questionId]
 * @returns {string}
 */
function labelForAnswerValue(id, lang, questionId) {
  const key = String(id || '').trim();
  if (!key) return '';
  const scoped = questionId && ANSWER_LABELS_BY_QUESTION[questionId]
    ? ANSWER_LABELS_BY_QUESTION[questionId][key]
    : null;
  const row = scoped || FLAT_ANSWER_LABELS[key] || null;
  return rowText(row, lang);
}

/** Sentence-case label for prose. Empty when the id is unknown or ambiguous. */
function proseLabelForAnswerValue(id, lang, questionId) {
  return proseCase(labelForAnswerValue(id, lang, questionId));
}

/** Bilingual prose label, with fallbacks when the id is unknown. */
function localizedAnswerLabel(id, questionId, fallbackEn, fallbackPl) {
  const en = proseLabelForAnswerValue(id, 'en', questionId);
  const pl = proseLabelForAnswerValue(id, 'pl', questionId);
  return {
    en: en || fallbackEn,
    pl: pl || fallbackPl || fallbackEn,
  };
}

function labelForUnknownField(id, lang) {
  const key = String(id || '').trim();
  if (!key) return '';
  const field = UNKNOWN_FIELD_LABELS[key];
  if (field) return rowText(field, lang);
  const asValue = labelForAnswerValue(key, lang);
  if (asValue) return proseCase(asValue);
  return key.replace(/_/g, ' ');
}

function formatConfidenceLabel(confidence, lang) {
  const key = String(confidence || '').trim().toUpperCase();
  if (!key) return '';
  return rowText(CONFIDENCE_LABELS[key], lang) || key;
}

function replaceKnownId(id, lang, questionId) {
  return proseLabelForAnswerValue(id, lang, questionId) || labelForAnswerValue(id, lang) || '';
}

/**
 * Rewrite user-visible prose that still contains raw answer ids.
 * Safe to run more than once. Does not replace ordinary words such as "batch jobs".
 * @param {string|null|undefined} text
 * @param {'en'|'pl'|string} [lang]
 * @returns {string}
 */
function humanizeAnswerTokens(text, lang) {
  let s = String(text || '');
  if (!s.trim()) return s;

  const swap = (id, questionId) => replaceKnownId(id, lang, questionId);

  s = s.replace(/\bfor ([a-z][a-z0-9_]*) users\b/g, (m, id) => {
    const label = swap(id, 'users');
    return label ? `for ${label}` : m;
  });
  s = s.replace(/\s+at ([a-z][a-z0-9_]*) stage\b/gi, (m, id) => {
    const label = swap(id, 'stage');
    return label ? `, currently ${label}` : m;
  });
  s = s.replace(/użytkowników:\s*([a-z][a-z0-9_]*)/g, (m, id) => {
    const label = swap(id, 'users');
    return label ? label : m;
  });
  s = s.replace(/użytkowników\s*\(([a-z][a-z0-9_]*)\)/g, (m, id) => {
    const label = swap(id, 'users');
    return label ? label : m;
  });
  s = s.replace(/\betap:\s*([a-z][a-z0-9_]*)/g, (m, id) => {
    const label = swap(id, 'stage');
    return label ? label : m;
  });
  s = s.replace(/na etapie\s+([a-z][a-z0-9_]*)/g, (m, id) => {
    const label = swap(id, 'stage');
    return label ? `na etapie ${label}` : m;
  });

  s = s.replace(/\(([a-z][a-z0-9_]*)\)/g, (m, id) => {
    const label = swap(id);
    if (!label || label.toLowerCase() === id) return m;
    return `(${label})`;
  });

  const snakeIds = Object.keys(FLAT_ANSWER_LABELS)
    .filter((id) => id.includes('_'))
    .sort((a, b) => b.length - a.length);
  for (const id of snakeIds) {
    const label = swap(id);
    if (!label) continue;
    const re = new RegExp(`(?<![A-Za-z0-9_])${id}(?![A-Za-z0-9_])`, 'g');
    s = s.replace(re, label);
  }

  s = s.replace(/\bA (AI|API|IoT|MVP)\b/g, 'An $1');
  s = s.replace(/\ba (AI|API|IoT|MVP)\b/g, 'an $1');
  return s;
}

/**
 * Model context: human values, keys kept so the prompt stays structured.
 * @param {Record<string, string>|null|undefined} answers
 * @returns {string}
 */
function formatAnswersForModel(answers) {
  return Object.entries(answers || {})
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([key, value]) => {
      const shown = String(value)
        .split(/[,|]/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((id) => proseLabelForAnswerValue(id, 'en', key) || labelForAnswerValue(id, 'en') || id)
        .join(', ');
      return `${key}: ${shown}`;
    })
    .join('; ');
}

function hasQuestionOptionLabel(questionId, optionId) {
  return Boolean(
    ANSWER_LABELS_BY_QUESTION[questionId]
    && ANSWER_LABELS_BY_QUESTION[questionId][optionId]
  );
}

// BA-light outcome options live in businessOutcome.js — merge so humanize / copy eval stay single-SoT.
const {
  answerLabelsForOutcome,
  answerLabelsForSuccess,
} = require('./businessOutcome');
Object.assign(ANSWER_LABELS_BY_QUESTION, {
  business_outcome: answerLabelsForOutcome(),
  success_signal: answerLabelsForSuccess(),
});

module.exports = {
  ANSWER_LABELS_BY_QUESTION,
  FLAT_ANSWER_LABELS,
  UNKNOWN_FIELD_LABELS,
  labelForAnswerValue,
  proseLabelForAnswerValue,
  localizedAnswerLabel,
  labelForUnknownField,
  formatConfidenceLabel,
  humanizeAnswerTokens,
  formatAnswersForModel,
  hasQuestionOptionLabel,
  proseCase,
};
